import React, { useState, useRef, useCallback, useEffect } from 'react'
import ReactDOM from 'react-dom'
import toast from 'react-hot-toast'
import AudioRecordingService from '../../services/audioRecordingService'

/**
 * AudioRecordingModal Component
 * 
 * Modal component for audio recording functionality.
 * Similar structure to DrawingModal but designed for audio recording interface.
 */
const AudioRecordingModal = ({ 
  isOpen, 
  onClose, 
  onDone
}) => {
  // Audio recording service
  const recordingServiceRef = useRef(new AudioRecordingService())
  
  // State management
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [audioBlob, setAudioBlob] = useState(null)
  const [audioUrl, setAudioUrl] = useState(null)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [audioLevel, setAudioLevel] = useState({ average: 0, peak: 0, rms: 0 })
  const [isProcessing, setIsProcessing] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasPermission, setHasPermission] = useState(false)
  const [recordingWaveform, setRecordingWaveform] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [error, setError] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [recordingChunks, setRecordingChunks] = useState([])

  // Refs
  const audioRef = useRef(null)
  const durationIntervalRef = useRef(null)
  const playbackIntervalRef = useRef(null)

  /**
   * Initialize recording service callbacks
   */
  useEffect(() => {
    const service = recordingServiceRef.current
    
    service.setCallbacks({
      onDataAvailable: (data) => {
        // Handle data available if needed
      },
      onStop: (blob) => {
        console.log('🎵 Recording stopped, blob received:', blob)
        setAudioBlob(blob)
        if (blob) {
          // Preserve the final recording duration before stopping
          const service = recordingServiceRef.current
          const finalDuration = service.getRecordingDuration()
          console.log('🎵 Final recording duration:', finalDuration)
          setRecordingDuration(finalDuration)
          
          const url = URL.createObjectURL(blob)
          console.log('🎵 Created audio URL:', url)
          setAudioUrl(url)
          
          // Force duration detection after URL is set
          setTimeout(() => {
            console.log('🎵 Attempting to detect duration after URL creation')
            if (audioRef.current) {
              console.log('🎵 Audio element exists, duration:', audioRef.current.duration)
              if (audioRef.current.duration && !isNaN(audioRef.current.duration)) {
                setCurrentTime(0)
                console.log('🎵 Duration detected, updating state')
              }
            }
          }, 100)
        }
        setIsRecording(false)
        setIsPaused(false)
      },
      onError: (error) => {
        console.error('❌ Recording error:', error)
        setError(`Recording failed: ${error.message}`)
        toast.error(`Recording error: ${error.message}`)
        setIsRecording(false)
        setIsPaused(false)
        setIsProcessing(false)
      },
      onLevelChange: (levelData) => {
        setAudioLevel(levelData)
        
        // Update waveform visualization
        if (levelData.raw && levelData.raw.length > 0) {
          setRecordingWaveform(prev => {
            const newData = Array.from(levelData.raw.slice(0, 32)) // Use first 32 frequency bins
            const updated = [...prev, newData]
            return updated.slice(-100) // Keep last 100 samples for performance
          })
        }
      }
    })

    return () => {
      // Cleanup on unmount and memory management
      service.cleanup()
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl)
      }
      
      // Cleanup recording chunks to prevent memory leaks
      if (recordingChunks.length > 0) {
        recordingChunks.forEach(chunk => {
          if (chunk && chunk.size > 0) {
            // Release chunk memory
            chunk = null
          }
        })
        setRecordingChunks([])
      }
      
      // Stop all intervals
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
      }
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current)
      }
    }
  }, [audioUrl])

  /**
   * Start duration monitoring
   */
  const startDurationMonitoring = useCallback(() => {
    durationIntervalRef.current = setInterval(() => {
      const service = recordingServiceRef.current
      const currentDuration = service.getRecordingDuration()
      setDuration(currentDuration)
      setRecordingDuration(currentDuration) // Store for playback fallback
    }, 1000)
  }, [])

  /**
   * Stop duration monitoring
   */
  const stopDurationMonitoring = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
      durationIntervalRef.current = null
    }
  }, [])

  /**
   * Start playback monitoring
   */
  const startPlaybackMonitoring = useCallback(() => {
    playbackIntervalRef.current = setInterval(() => {
      if (audioRef.current) {
        setCurrentTime(audioRef.current.currentTime)
      }
    }, 100)
  }, [])

  /**
   * Stop playback monitoring
   */
  const stopPlaybackMonitoring = useCallback(() => {
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current)
      playbackIntervalRef.current = null
    }
  }, [])

  /**
   * Handle record button click
   */
  const handleRecord = useCallback(async () => {
    try {
      const service = recordingServiceRef.current
      await service.startRecording()
      setIsRecording(true)
      setIsPaused(false)
      startDurationMonitoring()
      toast.success('Recording started')
    } catch (error) {
      console.error('❌ Failed to start recording:', error)
      toast.error(`Failed to start recording: ${error.message}`)
    }
  }, [startDurationMonitoring])

  /**
   * Handle stop button click
   */
  const handleStop = useCallback(() => {
    const service = recordingServiceRef.current
    service.stopRecording()
    stopDurationMonitoring()
    toast.success('Recording stopped')
  }, [stopDurationMonitoring])

  /**
   * Handle pause/resume button click
   */
  const handlePauseResume = useCallback(() => {
    const service = recordingServiceRef.current
    
    if (isPaused) {
      service.resumeRecording()
      setIsPaused(false)
      startDurationMonitoring()
      toast.success('Recording resumed')
    } else {
      service.pauseRecording()
      setIsPaused(true)
      stopDurationMonitoring()
      toast.success('Recording paused')
    }
  }, [isPaused, startDurationMonitoring, stopDurationMonitoring])

  /**
   * Handle play button click
   */
  const handlePlay = useCallback(() => {
    if (!audioRef.current || !audioUrl) return

    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
      stopPlaybackMonitoring()
    } else {
      audioRef.current.play()
      setIsPlaying(true)
      startPlaybackMonitoring()
    }
  }, [isPlaying, audioUrl, startPlaybackMonitoring, stopPlaybackMonitoring])

  /**
   * Handle audio time update
   */
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }, [])

  /**
   * Handle audio ended
   */
  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false)
    setCurrentTime(0)
    stopPlaybackMonitoring()
  }, [stopPlaybackMonitoring])

  /**
   * Handle audio metadata loaded
   */
  const handleAudioLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      console.log('Audio metadata loaded, duration:', audioRef.current.duration)
      // Force re-render to update slider max value and duration display
      if (audioRef.current.duration && !isNaN(audioRef.current.duration)) {
        setCurrentTime(0)
        // Force a state update to trigger re-render
        setTimeout(() => {
          if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime || 0)
          }
        }, 100)
      }
    }
  }, [])

  /**
   * Handle audio loaded data
   */
  const handleAudioLoadedData = useCallback(() => {
    if (audioRef.current) {
      console.log('Audio data loaded, duration:', audioRef.current.duration)
      // Force re-render when data is fully loaded
      if (audioRef.current.duration && !isNaN(audioRef.current.duration)) {
        setCurrentTime(audioRef.current.currentTime || 0)
      }
    }
  }, [])

  /**
   * Handle slider change
   */
  const handleSliderChange = useCallback((e) => {
    if (!audioRef.current || !audioRef.current.duration) return
    
    const newTime = parseFloat(e.target.value)
    if (!isNaN(newTime) && newTime >= 0) {
      audioRef.current.currentTime = newTime
      setCurrentTime(newTime)
    }
  }, [])

  /**
   * Handle slider mouse down
   */
  const handleSliderMouseDown = useCallback(() => {
    setIsDragging(true)
  }, [])

  /**
   * Handle slider mouse up
   */
  const handleSliderMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  /**
   * Handle done button click
   */
  const handleDone = useCallback(async () => {
    if (!audioBlob) {
      setError('No audio recorded')
      toast.error('Please record some audio before saving')
      return
    }

    // Validate file size (limit to 50MB)
    const maxSizeBytes = 50 * 1024 * 1024 // 50MB
    if (audioBlob.size > maxSizeBytes) {
      const sizeMB = (audioBlob.size / (1024 * 1024)).toFixed(1)
      setError(`Audio file too large: ${sizeMB}MB. Maximum size is 50MB.`)
      toast.error(`Audio file too large: ${sizeMB}MB. Maximum size is 50MB.`)
      return
    }

    // Validate minimum duration (at least 1 second)
    if (duration < 1) {
      setError('Recording too short. Please record at least 1 second of audio.')
      toast.error('Recording too short. Please record at least 1 second of audio.')
      return
    }
    
    setIsProcessing(true)
    setIsUploading(true)
    setError(null)
    
    try {
      // Create audio file with proper naming
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      const audioFile = new File([audioBlob], `audio-recording-${timestamp}.webm`, {
        type: audioBlob.type || 'audio/webm'
      })

      console.log('🎵 Saving audio file:', {
        name: audioFile.name,
        size: audioFile.size,
        sizeMB: (audioFile.size / (1024 * 1024)).toFixed(2),
        type: audioFile.type,
        duration: duration
      })

      // Call parent's done handler with audio file
      await onDone(audioFile)
      
    } catch (error) {
      console.error('❌ Failed to save audio:', error)
      setError(`Failed to save audio recording: ${error.message}`)
      toast.error(`Failed to save audio recording: ${error.message}`)
    } finally {
      setIsProcessing(false)
      setIsUploading(false)
    }
  }, [audioBlob, duration, onDone])

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    if (isProcessing) return
    
    // Clean up
    stopDurationMonitoring()
    stopPlaybackMonitoring()
    
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl)
    }
    
    recordingServiceRef.current.cleanup()
    
    // Reset state
    setIsRecording(false)
    setIsPaused(false)
    setAudioBlob(null)
    setAudioUrl(null)
    setDuration(0)
    setCurrentTime(0)
    setAudioLevel(0)
    setIsPlaying(false)
    
    onClose()
  }, [isProcessing, onClose, audioUrl, stopDurationMonitoring, stopPlaybackMonitoring])

  /**
   * Check microphone permission on mount
   */
  useEffect(() => {
    if (isOpen) {
      const checkPermission = async () => {
        try {
          const service = recordingServiceRef.current
          const hasPermission = await service.requestMicrophonePermission()
          setHasPermission(hasPermission)
          
          if (!hasPermission) {
            toast.error('Microphone permission is required for audio recording')
          }
        } catch (error) {
          console.error('❌ Permission check failed:', error)
          setHasPermission(false)
        }
      }
      
      checkPermission()
    }
  }, [isOpen])

  /**
   * Force re-render when audio URL changes to ensure duration is properly detected
   */
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      console.log('🎵 Audio URL changed, attempting duration detection')
      // Reset current time when new audio is loaded
      setCurrentTime(0)
      
      // Multiple attempts to detect duration
      const attempts = [100, 300, 500, 1000] // Try at different intervals
      
      const timeoutIds = attempts.map(delay => 
        setTimeout(() => {
          if (audioRef.current) {
            console.log(`🎵 Duration check attempt at ${delay}ms:`, audioRef.current.duration)
            if (audioRef.current.duration && !isNaN(audioRef.current.duration) && audioRef.current.duration > 0) {
              console.log('🎵 Duration successfully detected:', audioRef.current.duration)
              // Force re-render by updating current time
              setCurrentTime(audioRef.current.currentTime || 0)
            } else {
              // Try to trigger metadata loading
              if (audioRef.current.load) {
                console.log('🎵 Attempting to reload audio metadata')
                audioRef.current.load()
              }
            }
          }
        }, delay)
      )
      
      return () => {
        timeoutIds.forEach(id => clearTimeout(id))
      }
    }
  }, [audioUrl])

  if (!isOpen) return null

  const formatTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds) || seconds === Infinity) {
      return '0:00'
    }
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const getAudioLevelPercentage = () => {
    return Math.min((audioLevel.average / 255) * 100, 100)
  }

  const getPeakLevelPercentage = () => {
    return Math.min((audioLevel.peak / 255) * 100, 100)
  }

  const getRMSLevelPercentage = () => {
    return Math.min((audioLevel.rms / 255) * 100, 100)
  }

  /**
   * Get safe audio duration (avoid NaN and Infinity)
   */
  const getSafeAudioDuration = () => {
    // Use the duration state directly (already showing correct value like "00:15")
    if (duration > 0 && isFinite(duration)) {
      return duration
    }
    // Fallback to audio element duration (check for Infinity)
    if (audioRef.current && audioRef.current.duration && 
        !isNaN(audioRef.current.duration) && 
        isFinite(audioRef.current.duration) && 
        audioRef.current.duration > 0) {
      return audioRef.current.duration
    }
    // Final fallback to recording duration
    if (recordingDuration > 0 && isFinite(recordingDuration)) {
      console.log('🎵 Using recording duration as fallback:', recordingDuration)
      return recordingDuration
    }
    return 0
  }

  return ReactDOM.createPortal(
    <>
      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
          border: 2px solid white;
          transition: all 0.2s ease;
        }
        .slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.6);
        }
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
          transition: all 0.2s ease;
        }
        .slider::-moz-range-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.6);
        }
        .audio-level-bar {
          background: linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #10b981 100%);
          transition: width 0.1s ease;
        }
        
        /* Mobile optimizations */
        @media (max-width: 640px) {
          .slider::-webkit-slider-thumb {
            height: 24px;
            width: 24px;
          }
          .slider::-moz-range-thumb {
            height: 24px;
            width: 24px;
          }
          .mobile-touch-target {
            min-height: 44px;
            min-width: 44px;
          }
          
          /* Larger touch targets for mobile */
          .mobile-button {
            min-height: 48px;
            min-width: 48px;
            padding: 12px;
          }
          
          /* Better spacing on mobile */
          .mobile-spacing {
            gap: 16px;
          }
          
          /* Prevent zoom on input focus */
          input, textarea, select {
            font-size: 16px;
          }
        }
        
        /* Enhanced button animations */
        .recording-button {
          position: relative;
          overflow: hidden;
        }
        .recording-button::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: width 0.6s, height 0.6s;
        }
        .recording-button:hover::before {
          width: 100%;
          height: 100%;
        }
        
        /* Waveform animation */
        .waveform-bar {
          animation: waveform-pulse 0.1s ease-in-out;
        }
        @keyframes waveform-pulse {
          0% { opacity: 0.7; }
          50% { opacity: 1; }
          100% { opacity: 0.7; }
        }
        
        /* Modal Opacity for All Themes */
        .amoled-audio-modal {
          background-color: rgba(255, 255, 255, 0.5) !important;
          backdrop-filter: blur(10px) !important;
        }
        
        .dark .amoled-audio-modal {
          background-color: rgba(31, 41, 55, 0.5) !important;
        }
        
        /* AMOLED Audio Modal Styles */
        .amoled .amoled-audio-modal {
          background-color: rgba(0, 0, 0, 0.5) !important;
          backdrop-filter: blur(10px) brightness(0.1) !important;
          border-color: #262626 !important;
        }
        
        .amoled .fixed.inset-0.bg-black\/20 {
          background-color: rgba(0, 0, 0, 0.95) !important;
        }
        
        .amoled .amoled-audio-modal .bg-gradient-to-r {
          background: linear-gradient(90deg, rgba(0, 0, 0, 0.9), rgba(10, 10, 10, 0.9)) !important;
          border-color: #262626 !important;
        }
        
        .amoled .amoled-audio-modal .dark\\:border-gray-700\\/30 {
          border-color: #262626 !important;
        }
        
        .amoled .amoled-audio-modal .dark\\:text-gray-300 {
          color: #EDE0CF !important;
        }
        
        .amoled .amoled-audio-modal .dark\\:text-gray-400 {
          color: #a3a3a3 !important;
        }
        
        .amoled .amoled-audio-modal .dark\\:bg-gray-700 {
          background-color: #171717 !important;
        }
        
        .amoled .amoled-audio-modal .dark\\:bg-gray-800 {
          background-color: #0a0a0a !important;
        }
        
        .amoled .amoled-audio-modal .from-gray-50\\/20,
        .amoled .amoled-audio-modal .to-gray-100\\/20 {
          background: linear-gradient(90deg, rgba(0, 0, 0, 0.9), rgba(10, 10, 10, 0.9)) !important;
        }
      `}</style>
      <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[60] p-2">
        <div className="bg-white/40 dark:bg-gray-800/40 backdrop-blur-[10px] rounded-xl shadow-2xl border border-white/30 dark:border-gray-700/30 max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col mx-2 amoled-audio-modal" style={{ backdropFilter: 'blur(10px)' }}>
          {/* Modal Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg flex items-center justify-center shadow-lg">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Audio Recorder
              </h3>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
              title="Close"
              disabled={isProcessing}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mx-4 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
              </div>
            </div>
          )}

          {/* Recording Controls */}
          <div className="px-4 py-4 flex-shrink-0">
            <div className="space-y-4">
              {/* Compact Audio Visualization */}
              <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Level</span>
                <div className="flex items-center gap-2">
                  {duration > 600 && isRecording && (
                    <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                      ⚠️ Long recording
                    </span>
                  )}
                  <span className="text-xs text-gray-500 dark:text-gray-500">
                    {Math.round(getAudioLevelPercentage())}% | Peak: {Math.round(getPeakLevelPercentage())}%
                  </span>
                </div>
              </div>
                
                {/* Compact Audio Meter */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 relative overflow-hidden">
                  {/* RMS Level (background) */}
                  <div 
                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-400 to-yellow-400 rounded-full transition-all duration-75"
                    style={{ width: `${getRMSLevelPercentage()}%` }}
                  />
                  {/* Average Level (foreground) */}
                  <div 
                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-400 to-purple-400 rounded-full transition-all duration-75"
                    style={{ width: `${getAudioLevelPercentage()}%` }}
                  />
                  {/* Peak Indicator */}
                  {getPeakLevelPercentage() > 0 && (
                    <div 
                      className="absolute top-0 w-0.5 h-full bg-red-500 rounded-full transition-all duration-75"
                      style={{ left: `${getPeakLevelPercentage()}%` }}
                    />
                  )}
                </div>

                {/* Compact Waveform */}
                {isRecording && recordingWaveform.length > 0 && (
                  <div className="w-full h-8 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 flex items-end justify-center gap-0.5 overflow-hidden">
                    {recordingWaveform[recordingWaveform.length - 1]?.slice(0, 24).map((value, index) => {
                      const height = (value / 255) * 100
                      return (
                      <div
                        key={index}
                        className="waveform-bar bg-gradient-to-t from-blue-500 to-purple-400 rounded-sm transition-all duration-75"
                        style={{
                          width: '1.5px',
                          height: `${Math.max(height, 2)}%`,
                          minHeight: '2px'
                        }}
                      />
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Compact Duration Display */}
              <div className="text-center">
                <div className="text-2xl font-mono font-bold text-gray-900 dark:text-gray-100">
                  {formatTime(duration)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500">
                  {isRecording ? (isPaused ? 'Paused' : 'Recording...') : 'Ready'}
                </div>
              </div>

              {/* Compact Recording Controls */}
              <div className="flex justify-center items-center gap-4 mobile-spacing">
                {!isRecording ? (
                  <button
                    type="button"
                    onClick={handleRecord}
                    disabled={!hasPermission}
                    className="recording-button w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 active:scale-95 mobile-touch-target mobile-button"
                    title="Start Recording"
                  >
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                    </svg>
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handlePauseResume}
                      className={`mobile-touch-target w-12 h-12 ${isPaused 
                        ? 'bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' 
                        : 'bg-gradient-to-br from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700'
                      } text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 active:scale-95`}
                      title={isPaused ? "Resume Recording" : "Pause Recording"}
                    >
                      {isPaused ? (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      ) : (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                        </svg>
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={handleStop}
                      className="mobile-touch-target w-12 h-12 bg-gradient-to-br from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 active:scale-95"
                      title="Stop Recording"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <rect x="6" y="6" width="12" height="12"/>
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {/* Compact Recording Status */}
              {isRecording && (
                <div className="flex items-center justify-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'}`} />
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {isPaused ? 'Paused' : 'Recording'}
                  </span>
                </div>
              )}

              {/* Compact Playback Controls */}
              {audioUrl && (
                <div className="space-y-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                      Playback
                    </div>
                    
                    {/* Compact Playback Controls */}
                    <div className="flex items-center justify-center gap-4 mb-3">
                      <button
                        type="button"
                        onClick={handlePlay}
                        className="mobile-touch-target w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 active:scale-95"
                        title={isPlaying ? "Pause" : "Play"}
                      >
                        {isPlaying ? (
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                          </svg>
                        ) : (
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        )}
                      </button>
                      
                      <div className="text-center">
                        <div className="text-xs font-mono text-gray-600 dark:text-gray-400">
                          {formatTime(currentTime)} / {formatTime(getSafeAudioDuration())}
                        </div>
                      </div>
                    </div>

                    {/* Compact Audio Slider */}
                    <div className="space-y-2">
                      <input
                        type="range"
                        min="0"
                        max={getSafeAudioDuration()}
                        value={isNaN(currentTime) ? 0 : currentTime}
                        onChange={handleSliderChange}
                        onMouseDown={handleSliderMouseDown}
                        onMouseUp={handleSliderMouseUp}
                        className="slider w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, 
                            #3b82f6 0%, 
                            #3b82f6 ${getSafeAudioDuration() > 0 ? (currentTime / getSafeAudioDuration()) * 100 : 0}%, 
                            #e5e7eb ${getSafeAudioDuration() > 0 ? (currentTime / getSafeAudioDuration()) * 100 : 0}%, 
                            #e5e7eb 100%)`
                        }}
                      />
                      
                      {/* Compact Audio info */}
                      <div className="text-center">
                        <div className="text-xs text-gray-500 dark:text-gray-500">
                          {audioBlob && `${(audioBlob.size / 1024 / 1024).toFixed(2)} MB`}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hidden Audio Element */}
              {audioUrl && (
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={handleAudioEnded}
                  onLoadedMetadata={handleAudioLoadedMetadata}
                  onLoadedData={handleAudioLoadedData}
                  preload="metadata"
                />
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="px-4 py-3 border-t border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 hover:shadow-md transition-all duration-200"
                disabled={isProcessing || isUploading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDone}
                disabled={!audioBlob || isProcessing || isUploading}
                className="px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-1"
              >
                {isUploading ? (
                  <>
                    <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Uploading...</span>
                  </>
                ) : isProcessing ? (
                  <>
                    <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Audio</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}

export default AudioRecordingModal
